import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Megaphone, Plus, Search, Play, Pause, Square, Trash2, Pencil,
  History, FileText, Euro, Users, ChevronDown, X, CalendarDays,
  TrendingUp, AlertCircle, SkipForward, Loader2, Bell, BellOff, Copy
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../components/ui/alert-dialog";
import {
  getSubscriptions, getSubscriptionStats, createSubscription, updateSubscription,
  pauseSubscription, resumeSubscription, stopSubscription, deleteSubscription,
  generateSubscriptionInvoice, skipSubscriptionPeriod, getCustomers, createCustomer, sendTestPush, duplicateDocument
} from "../services/api";
import { isPushSupported, getPushSubscription, enablePush, disablePush } from "../utils/push";
import { CommTabs } from "../components/communication/CommTabs";
import { SubscriptionBatchDialog } from "../components/communication/SubscriptionBatchDialog";

const PLANS = {
  basic: { label: "BASIC", price: 300, badge: "bg-sky-100 text-sky-700 border border-sky-200", ring: "ring-sky-500", accent: "text-sky-600", bar: "bg-sky-500" },
  branding: { label: "BRANDING", price: 500, badge: "bg-indigo-100 text-indigo-700 border border-indigo-200", ring: "ring-indigo-500", accent: "text-indigo-600", bar: "bg-indigo-500" },
  visibilite: { label: "VISIBILITÉ", price: 800, badge: "bg-amber-100 text-amber-800 border border-amber-200", ring: "ring-amber-500", accent: "text-amber-600", bar: "bg-amber-500" },
};

const STATUS_CONFIG = {
  active: { label: "Actif", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  paused: { label: "En pause", badge: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  stopped: { label: "Arrêté", badge: "bg-red-100 text-red-700", dot: "bg-red-500" },
};

const TIMELINE_STYLES = {
  billed: { cls: "bg-emerald-500", title: "Facturé" },
  skipped: { cls: "bg-slate-300", title: "Mois passé (non facturé)" },
  paused: { cls: "bg-orange-400", title: "En pause" },
  stopped: { cls: "bg-red-300", title: "Arrêté" },
  due: { cls: "bg-red-500 animate-pulse", title: "À facturer !" },
  upcoming: { cls: "bg-white border-2 border-dashed border-slate-300", title: "Mois en cours (à venir)" },
};

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—";

// ---------- Timeline ----------
const Timeline = ({ timeline }) => {
  const items = timeline.slice(-12);
  return (
    <div className="flex items-end gap-1 flex-wrap" data-testid="subscription-timeline">
      {items.map((t) => {
        const style = TIMELINE_STYLES[t.status] || TIMELINE_STYLES.upcoming;
        return (
          <div key={t.period} className="group relative">
            <div className={`w-5 h-5 rounded ${style.cls} cursor-default`} />
            <div className="absolute bottom-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-20 capitalize">
              {t.label} · {style.title}{t.invoice_number ? ` (${t.invoice_number})` : ""}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------- Stat cards ----------
const StatsBar = ({ stats }) => {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      <div className="bg-[#26788A] text-white rounded-xl p-4" data-testid="stat-mrr">
        <div className="flex items-center gap-2 text-teal-100 text-xs font-medium uppercase tracking-wide"><TrendingUp size={14} /> Revenu mensuel (MRR)</div>
        <p className="text-2xl font-bold mt-1">{fmtEur(stats.mrr_ht)} <span className="text-sm font-normal text-teal-100">HT</span></p>
        <p className="text-[11px] text-teal-100 mt-0.5 capitalize">
          {new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })} · {stats.mrr_clients ?? 0} client{(stats.mrr_clients ?? 0) > 1 ? "s" : ""} actif{(stats.mrr_clients ?? 0) > 1 ? "s" : ""}
        </p>
        <p className="text-[11px] text-teal-100" data-testid="stat-mrr-subscriptions">{stats.mrr_subscriptions ?? stats.by_status.active} forfaits actifs</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="stat-active">
        <div className="flex items-center gap-2 text-emerald-600 text-xs font-medium uppercase tracking-wide"><Play size={14} /> Actifs</div>
        <p className="text-2xl font-bold text-slate-900 mt-1">{stats.by_status.active}</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="stat-paused">
        <div className="flex items-center gap-2 text-orange-500 text-xs font-medium uppercase tracking-wide"><Pause size={14} /> En pause</div>
        <p className="text-2xl font-bold text-slate-900 mt-1">{stats.by_status.paused}</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="stat-plans">
        <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wide"><Users size={14} /> Par forfait</div>
        <div className="flex gap-3 mt-2">
          {Object.entries(PLANS).map(([key, p]) => (
            <div key={key} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${p.bar}`} />
              <span className="text-sm font-semibold text-slate-700">{stats.by_plan[key] || 0}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- À facturer section ----------
const BillingDueSection = ({ subscriptions, onGenerate, onSkip, generating }) => {
  const due = subscriptions.filter((s) => s.status === "active" && s.periods_due?.length > 0);
  if (due.length === 0) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6" data-testid="billing-due-section">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle size={18} className="text-red-600" />
        <h2 className="font-semibold text-red-800">À facturer — validation manuelle</h2>
        <Badge className="bg-red-600 text-white">{due.reduce((n, s) => n + s.periods_due.length, 0)}</Badge>
      </div>
      <p className="text-xs text-red-700/70 mb-3 -mt-1">Le mois en cours se génère automatiquement en brouillon à la date anniversaire. Les mois plus anciens se valident ici.</p>
      <div className="space-y-2">
        {due.map((sub) =>
          sub.periods_due.map((p) => (
            <div key={`${sub.id}-${p.period}`} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-red-100 px-3 py-2" data-testid={`due-item-${sub.id}-${p.period}`}>
              <div className="flex-1 min-w-[180px]">
                <span className="font-medium text-slate-900">{sub.customer_name}</span>
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${PLANS[sub.plan]?.badge}`}>{PLANS[sub.plan]?.label}</span>
                <p className="text-xs text-slate-500 capitalize mt-0.5">{p.label} · {fmtEur(sub.monthly_price_ht)} HT · facturation le {sub.anniversary_day} du mois</p>
              </div>
              <Button
                size="sm"
                className="bg-[#26788A] hover:bg-[#1D5E6C] text-white"
                disabled={generating === `${sub.id}-${p.period}`}
                onClick={() => onGenerate(sub, p.period)}
                data-testid={`generate-invoice-btn-${sub.id}-${p.period}`}
              >
                {generating === `${sub.id}-${p.period}` ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileText size={14} className="mr-1" />}
                Générer la facture
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-slate-500"
                onClick={() => onSkip(sub, p.period)}
                data-testid={`skip-period-btn-${sub.id}-${p.period}`}
              >
                <SkipForward size={14} className="mr-1" /> Passer ce mois
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ---------- Subscription card ----------
const SubscriptionCard = ({ sub, onAction }) => {
  const plan = PLANS[sub.plan] || PLANS.basic;
  const status = STATUS_CONFIG[sub.status] || STATUS_CONFIG.active;
  const dueCount = sub.periods_due?.length || 0;

  return (
    <div className={`bg-white border rounded-xl p-4 transition-shadow hover:shadow-md ${sub.status === "stopped" ? "border-slate-200 opacity-70" : dueCount > 0 ? "border-red-300" : "border-slate-200"}`} data-testid={`subscription-card-${sub.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 truncate">{sub.customer_name}</h3>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${plan.badge}`}>{plan.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${status.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />{status.label}
            </span>
            {dueCount > 0 && sub.status === "active" && (
              <Badge className="bg-red-600 text-white text-xs">{dueCount} mois à facturer</Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            <span className={`font-semibold ${plan.accent}`}>{fmtEur(sub.monthly_price_ht)} HT/mois</span>
            <span className="mx-1.5">·</span>
            Depuis le {fmtDate(sub.start_date)}
            <span className="mx-1.5">·</span>
            <span className="font-medium text-slate-700">{sub.months_active ?? sub.months_since_start} mois actif{(sub.months_active ?? 0) > 1 ? "s" : ""}</span>
          </p>
          {sub.notes && <p className="text-xs text-slate-500 mt-1 break-words" data-testid={`subscription-notes-${sub.id}`}>{sub.notes}</p>}
          <p className="text-xs text-slate-400 mt-0.5">
            {sub.months_billed} mois facturés ({fmtEur(sub.total_billed_ht)} HT)
            {sub.next_billing_date && sub.status === "active" && (
              <> · Prochaine facturation : {fmtDate(sub.next_billing_date)}</>
            )}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid={`subscription-actions-${sub.id}`}>
              Actions <ChevronDown size={14} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {sub.status === "active" && (
              <DropdownMenuItem onClick={() => onAction("pause", sub)} data-testid={`pause-btn-${sub.id}`}>
                <Pause size={14} className="mr-2 text-orange-500" /> Mettre en pause
              </DropdownMenuItem>
            )}
            {sub.status !== "active" && (
              <DropdownMenuItem onClick={() => onAction("resume", sub)} data-testid={`resume-btn-${sub.id}`}>
                <Play size={14} className="mr-2 text-emerald-600" /> {sub.status === "paused" ? "Reprendre" : "Réactiver"}
              </DropdownMenuItem>
            )}
            {sub.status !== "stopped" && (
              <DropdownMenuItem onClick={() => onAction("stop", sub)} data-testid={`stop-btn-${sub.id}`}>
                <Square size={14} className="mr-2 text-red-500" /> Arrêter le forfait
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAction("billing", sub)} data-testid={`billing-months-btn-${sub.id}`}>
              <FileText size={14} className="mr-2 text-slate-500" /> Factures par mois
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction("edit", sub)} data-testid={`edit-btn-${sub.id}`}>
              <Pencil size={14} className="mr-2 text-slate-500" /> Modifier / Changer forfait
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction("history", sub)} data-testid={`history-btn-${sub.id}`}>
              <History size={14} className="mr-2 text-slate-500" /> Historique
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAction("delete", sub)} className="text-red-600" data-testid={`delete-btn-${sub.id}`}>
              <Trash2 size={14} className="mr-2" /> Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-slate-400 font-medium">12 derniers mois</span>
          <div className="hidden sm:flex items-center gap-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500" />Facturé</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-500" />À facturer</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-orange-400" />Pause</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-slate-300" />Passé</span>
          </div>
        </div>
        <Timeline timeline={sub.timeline || []} />
      </div>
    </div>
  );
};

// ---------- Create / Edit Dialog ----------
const EMPTY_NEW_CLIENT = { customer_type: "professional", company_name: "", first_name: "", last_name: "", email: "", address: "", postal_code: "", city: "" };

const SubscriptionDialog = ({ open, onClose, onSaved, editSub }) => {
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState(EMPTY_NEW_CLIENT);
  const [plan, setPlan] = useState("basic");
  const [customPrice, setCustomPrice] = useState("");
  const [startDate, setStartDate] = useState("");
  const [billingDay, setBillingDay] = useState("");
  const [periods, setPeriods] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      getCustomers({ limit: 500 }).then((res) => setCustomers(res.data));
      if (editSub) {
        setPlan(editSub.plan);
        setCustomPrice(editSub.monthly_price_ht !== PLANS[editSub.plan]?.price ? String(editSub.monthly_price_ht) : "");
        setStartDate(editSub.start_date);
        setPeriods(
          editSub.periods?.length
            ? editSub.periods.map((p) => ({ start: p.start || "", end: p.end || "" }))
            : [{ start: editSub.start_date, end: "" }]
        );
        setNotes(editSub.notes || "");
        setBillingDay(editSub.billing_day ? String(editSub.billing_day) : "");
        setSelectedCustomer({ id: editSub.customer_id, name: editSub.customer_name });
      } else {
        setPlan("basic");
        setCustomPrice("");
        setStartDate(new Date().toISOString().slice(0, 10));
        setNotes("");
        setBillingDay("");
        setSelectedCustomer(null);
      }
      setShowNewClient(false);
      setNewClient(EMPTY_NEW_CLIENT);
      setCustomerSearch("");
    }
  }, [open, editSub]);

  const filteredCustomers = customers.filter((c) => {
    const name = (c.company_name || `${c.first_name || ""} ${c.last_name || ""}`).toLowerCase();
    return name.includes(customerSearch.toLowerCase());
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      let customerId = selectedCustomer?.id;

      if (showNewClient) {
        const isCompany = newClient.customer_type === "professional";
        if (isCompany && !newClient.company_name) { toast.error("Nom de l'entreprise requis"); return; }
        if (!isCompany && !newClient.last_name) { toast.error("Nom du client requis"); return; }
        const payload = { ...newClient };
        if (!payload.email) delete payload.email;
        const res = await createCustomer(payload);
        customerId = res.data.id;
      }

      if (!customerId) { toast.error("Sélectionnez un client"); return; }
      if (!startDate) { toast.error("Date de début requise"); return; }

      const price = customPrice !== "" ? parseFloat(customPrice) : PLANS[plan].price;
      const billingDayVal = billingDay !== "" ? parseInt(billingDay, 10) : null;
      if (billingDayVal !== null && (billingDayVal < 1 || billingDayVal > 31)) {
        toast.error("Le jour de facturation doit être entre 1 et 31");
        return;
      }

      if (editSub) {
        const cleanPeriods = periods.filter((p) => p.start);
        if (cleanPeriods.length === 0) { toast.error("Au moins une période avec une date de début est requise"); return; }
        for (const p of cleanPeriods) {
          if (p.end && p.end < p.start) { toast.error("Une date de fin est antérieure à sa date de début"); return; }
        }
        await updateSubscription(editSub.id, {
          plan,
          monthly_price_ht: price,
          notes,
          billing_day: billingDayVal,
          periods: cleanPeriods.map((p) => ({ start: p.start, end: p.end || null })),
        });
        toast.success("Abonnement mis à jour");
      } else {
        await createSubscription({ customer_id: customerId, plan, monthly_price_ht: price, start_date: startDate, billing_day: billingDayVal, notes });
        toast.success("Abonnement créé");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="responsive-dialog max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="subscription-dialog">
        <DialogHeader>
          <DialogTitle>{editSub ? "Modifier l'abonnement" : "Nouvel abonnement Communication"}</DialogTitle>
          <DialogDescription>Forfait mensuel sans engagement, facturé à la date anniversaire.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Client */}
          {!editSub && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Client</Label>
                <button
                  type="button"
                  className="text-xs text-[#26788A] font-medium hover:underline"
                  onClick={() => { setShowNewClient(!showNewClient); setSelectedCustomer(null); }}
                  data-testid="toggle-new-client-btn"
                >
                  {showNewClient ? "← Choisir un client existant" : "+ Nouveau client"}
                </button>
              </div>
              {!showNewClient ? (
                <div>
                  {selectedCustomer ? (
                    <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
                      <span className="text-sm font-medium text-slate-900">{selectedCustomer.name}</span>
                      <button onClick={() => setSelectedCustomer(null)} data-testid="clear-customer-btn"><X size={14} className="text-slate-400" /></button>
                    </div>
                  ) : (
                    <>
                      <Input
                        placeholder="Rechercher un client..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        data-testid="customer-search-input"
                      />
                      {customerSearch && (
                        <div className="border border-slate-200 rounded-md mt-1 max-h-40 overflow-y-auto divide-y divide-slate-100">
                          {filteredCustomers.slice(0, 10).map((c) => {
                            const name = c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
                            return (
                              <button
                                key={c.id}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                                onClick={() => { setSelectedCustomer({ id: c.id, name }); setCustomerSearch(""); }}
                                data-testid={`customer-option-${c.id}`}
                              >
                                {name}
                              </button>
                            );
                          })}
                          {filteredCustomers.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">Aucun client trouvé</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50">
                  <div className="flex gap-2">
                    <button
                      className={`flex-1 text-xs py-1.5 rounded-md border ${newClient.customer_type === "professional" ? "bg-[#26788A] text-white border-[#26788A]" : "bg-white text-slate-600 border-slate-200"}`}
                      onClick={() => setNewClient({ ...newClient, customer_type: "professional" })}
                    >Professionnel</button>
                    <button
                      className={`flex-1 text-xs py-1.5 rounded-md border ${newClient.customer_type === "individual" ? "bg-[#26788A] text-white border-[#26788A]" : "bg-white text-slate-600 border-slate-200"}`}
                      onClick={() => setNewClient({ ...newClient, customer_type: "individual" })}
                    >Particulier</button>
                  </div>
                  {newClient.customer_type === "professional" ? (
                    <Input placeholder="Nom de l'entreprise *" value={newClient.company_name} onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })} data-testid="new-client-company-input" />
                  ) : (
                    <div className="flex gap-2">
                      <Input placeholder="Prénom" value={newClient.first_name} onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })} />
                      <Input placeholder="Nom *" value={newClient.last_name} onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })} data-testid="new-client-lastname-input" />
                    </div>
                  )}
                  <Input placeholder="Email" type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
                  <Input placeholder="Adresse" value={newClient.address} onChange={(e) => setNewClient({ ...newClient, address: e.target.value })} />
                  <div className="flex gap-2">
                    <Input placeholder="Code postal" className="w-32" value={newClient.postal_code} onChange={(e) => setNewClient({ ...newClient, postal_code: e.target.value })} />
                    <Input placeholder="Ville" value={newClient.city} onChange={(e) => setNewClient({ ...newClient, city: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
          )}
          {editSub && (
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700">
              Client : <span className="font-medium">{editSub.customer_name}</span>
            </div>
          )}

          {/* Forfait */}
          <div>
            <Label className="mb-1.5 block">Forfait</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(PLANS).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setPlan(key); setCustomPrice(""); }}
                  className={`rounded-lg border-2 p-3 text-center transition-all ${plan === key ? `ring-2 ${p.ring} border-transparent bg-white shadow-sm` : "border-slate-200 bg-slate-50 hover:bg-white"}`}
                  data-testid={`plan-option-${key}`}
                >
                  <span className={`text-xs font-bold block ${p.accent}`}>{p.label}</span>
                  <span className="text-sm font-semibold text-slate-900 block mt-1">{p.price}€</span>
                  <span className="text-[10px] text-slate-400">/mois HT</span>
                </button>
              ))}
            </div>
          </div>

          {/* Prix personnalisé + date/périodes */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <Label className="mb-1.5 block">Prix personnalisé (optionnel)</Label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder={`${PLANS[plan].price} (défaut)`}
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  data-testid="custom-price-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">€ HT/mois</span>
              </div>
            </div>
            {!editSub && (
              <div className="flex-1">
                <Label className="mb-1.5 block">Date de début</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" />
              </div>
            )}
          </div>

          {/* Jour de facturation */}
          <div>
            <Label className="mb-1.5 block">Jour de facturation dans le mois (optionnel)</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                max={31}
                className="w-28"
                placeholder={startDate ? String(new Date(startDate + "T00:00:00").getDate()) : "—"}
                value={billingDay}
                onChange={(e) => setBillingDay(e.target.value)}
                data-testid="billing-day-input"
              />
              <p className="text-[11px] text-slate-400 flex-1">
                {billingDay
                  ? `Facturation le ${billingDay} de chaque mois`
                  : startDate
                    ? `Vide = date anniversaire (le ${new Date(startDate + "T00:00:00").getDate()} de chaque mois)`
                    : "Vide = date anniversaire du début de forfait"}
              </p>
            </div>
          </div>

          {/* Périodes multiples (édition) */}
          {editSub && (
            <div data-testid="periods-editor">
              <Label className="mb-1 block">Périodes d'abonnement</Label>
              <p className="text-[11px] text-slate-400 mb-2">
                Si le client a arrêté puis repris, ajoutez plusieurs périodes. Les mois hors période ne sont ni comptés ni facturés. Laissez la fin vide si le forfait est en cours.
              </p>
              <div className="space-y-2">
                {periods.map((p, i) => (
                  <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2" data-testid={`period-row-${i}`}>
                    <Input
                      type="date"
                      className="w-full sm:flex-1 min-w-0"
                      value={p.start}
                      onChange={(e) => setPeriods(periods.map((x, j) => j === i ? { ...x, start: e.target.value } : x))}
                      data-testid={`period-start-${i}`}
                    />
                    <span className="text-slate-400 text-sm">→</span>
                    <div className="relative flex-1">
                      <Input
                        type="date"
                        value={p.end}
                        onChange={(e) => setPeriods(periods.map((x, j) => j === i ? { ...x, end: e.target.value } : x))}
                        data-testid={`period-end-${i}`}
                      />
                      {!p.end && <span className="absolute right-9 top-1/2 -translate-y-1/2 text-[10px] text-emerald-600 font-medium pointer-events-none bg-white px-1">En cours</span>}
                    </div>
                    <button
                      type="button"
                      className="text-slate-300 hover:text-red-500 shrink-0"
                      onClick={() => setPeriods(periods.filter((_, j) => j !== i))}
                      disabled={periods.length === 1}
                      data-testid={`period-remove-${i}`}
                    >
                      <X size={15} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="mt-2 text-xs text-[#26788A] font-medium hover:underline flex items-center gap-1"
                onClick={() => setPeriods([...periods, { start: "", end: "" }])}
                data-testid="add-period-btn"
              >
                <Plus size={13} /> Ajouter une période
              </button>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="mb-1.5 block">Notes (optionnel)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Infos internes sur ce client / forfait..." data-testid="notes-input" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="cancel-subscription-btn">Annuler</Button>
          <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={handleSave} disabled={saving} data-testid="save-subscription-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-1" />}
            {editSub ? "Enregistrer" : "Créer l'abonnement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Status change dialog (pause / stop / resume) ----------
const STATUS_ACTIONS = {
  pause: { title: "Mettre en pause", desc: "Le client fait une pause : aucune facture ne sera proposée tant que le forfait est en pause.", verb: "Mettre en pause", fn: pauseSubscription, color: "bg-orange-500 hover:bg-orange-600" },
  resume: { title: "Reprendre le forfait", desc: "Le forfait redevient actif : la facturation mensuelle reprend à partir de la date d'effet.", verb: "Reprendre", fn: resumeSubscription, color: "bg-emerald-600 hover:bg-emerald-700" },
  stop: { title: "Arrêter le forfait", desc: "Le client ne reconduit pas : le forfait est arrêté définitivement (réactivable si besoin).", verb: "Arrêter", fn: stopSubscription, color: "bg-red-600 hover:bg-red-700" },
};

const StatusDialog = ({ action, sub, onClose, onSaved }) => {
  const [note, setNote] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const config = STATUS_ACTIONS[action];

  useEffect(() => {
    if (action) { setNote(""); setEffectiveDate(new Date().toISOString().slice(0, 10)); }
  }, [action, sub]);

  if (!config || !sub) return null;

  const handleConfirm = async () => {
    try {
      setSaving(true);
      await config.fn(sub.id, { note: note || undefined, effective_date: effectiveDate });
      toast.success(`${config.title} : ${sub.customer_name}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!action} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="status-dialog">
        <DialogHeader>
          <DialogTitle>{config.title} — {sub.customer_name}</DialogTitle>
          <DialogDescription>{config.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 block">Date d'effet</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="effective-date-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">Note (optionnel)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Raison, contexte..." data-testid="status-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button className={`text-white ${config.color}`} onClick={handleConfirm} disabled={saving} data-testid="confirm-status-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-1" />}
            {config.verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Factures par mois dialog ----------
const MONTH_STATUS_BADGES = {
  billed: { label: "Facturée", cls: "bg-emerald-100 text-emerald-700" },
  skipped: { label: "Passé (non facturé)", cls: "bg-slate-200 text-slate-600" },
  due: { label: "À facturer", cls: "bg-red-100 text-red-700" },
  upcoming: { label: "Mois en cours", cls: "bg-teal-50 text-teal-700" },
  paused: { label: "En pause", cls: "bg-orange-100 text-orange-700" },
  stopped: { label: "Arrêté", cls: "bg-red-50 text-red-500" },
};

const MonthBillingDialog = ({ sub, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  if (!sub) return null;
  const months = [...(sub.timeline || [])].reverse();

  const handleGenerate = async (period) => {
    try {
      setBusy(period);
      const res = await generateSubscriptionInvoice(sub.id, period);
      toast.success(res.data.message, {
        action: { label: "Ouvrir", onClick: () => navigate(`/document/${res.data.invoice_id}`) },
      });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  const handleDuplicate = async (invoiceId, period) => {
    try {
      setBusy(period);
      const res = await duplicateDocument(invoiceId);
      toast.success("Copie de la facture créée (brouillon)", {
        action: { label: "Ouvrir", onClick: () => navigate(`/document/${res.data.id}`) },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la copie");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={!!sub} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="month-billing-dialog">
        <DialogHeader>
          <DialogTitle>Factures par mois — {sub.customer_name}</DialogTitle>
          <DialogDescription>
            Générez la facture d'un mois précis, ou copiez une facture déjà émise.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {months.map((m) => {
            const badge = MONTH_STATUS_BADGES[m.status] || MONTH_STATUS_BADGES.upcoming;
            return (
              <div key={m.period} className="flex items-center gap-2 bg-slate-50 rounded-md px-3 py-2" data-testid={`billing-month-${m.period}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-slate-800 capitalize">{m.label}</span>
                  <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                  {m.status === "billed" && (
                    <p className="text-xs text-slate-400 mt-0.5">{m.invoice_number || "Brouillon"} · {fmtEur(sub.monthly_price_ht)} HT</p>
                  )}
                </div>
                {m.status === "billed" ? (
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/document/${m.invoice_id}`)} data-testid={`view-invoice-${m.period}`}>
                      <FileText size={12} className="mr-1" /> Voir
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === m.period} onClick={() => handleDuplicate(m.invoice_id, m.period)} data-testid={`copy-invoice-${m.period}`}>
                      {busy === m.period ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} className="mr-1" />} Copier
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" className="h-7 text-xs bg-[#26788A] hover:bg-[#1D5E6C] text-white" disabled={busy === m.period} onClick={() => handleGenerate(m.period)} data-testid={`generate-month-${m.period}`}>
                    {busy === m.period ? <Loader2 size={12} className="animate-spin mr-1" /> : <FileText size={12} className="mr-1" />} Générer la facture
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- History dialog ----------
const HistoryDialog = ({ sub, onClose }) => {
  const navigate = useNavigate();
  if (!sub) return null;
  const history = [...(sub.status_history || [])].reverse();
  const billed = [...(sub.billed_periods || [])].reverse();

  return (
    <Dialog open={!!sub} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle>Historique — {sub.customer_name}</DialogTitle>
          <DialogDescription>Forfait {PLANS[sub.plan]?.label} · depuis le {fmtDate(sub.start_date)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><CalendarDays size={14} /> Changements de statut</h4>
            <div className="space-y-1.5">
              {history.map((h, i) => {
                const st = STATUS_CONFIG[h.status] || STATUS_CONFIG.active;
                return (
                  <div key={i} className="flex items-start gap-2 text-sm bg-slate-50 rounded-md px-3 py-2">
                    <span className={`w-2 h-2 rounded-full mt-1.5 ${st.dot}`} />
                    <div className="flex-1">
                      <span className="font-medium text-slate-800">{st.label}</span>
                      <span className="text-slate-400 text-xs ml-2">{fmtDate(h.date)}</span>
                      {h.note && <p className="text-xs text-slate-500">{h.note}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Euro size={14} /> Mois facturés ({sub.months_billed})</h4>
            {billed.length === 0 && <p className="text-sm text-slate-400">Aucune facture générée pour le moment.</p>}
            <div className="space-y-1.5">
              {billed.map((b) => (
                <div key={b.period} className="flex items-center justify-between text-sm bg-slate-50 rounded-md px-3 py-2">
                  <div>
                    <span className="font-medium text-slate-800 capitalize">{b.period && new Date(b.period + "-01T00:00:00").toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
                    {b.skipped ? (
                      <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">Passé</Badge>
                    ) : (
                      <span className="text-slate-400 text-xs ml-2">{fmtEur(b.amount_ht)} HT</span>
                    )}
                  </div>
                  {b.invoice_id && (
                    <Button variant="ghost" size="sm" className="text-[#26788A] h-7" onClick={() => navigate(`/document/${b.invoice_id}`)}>
                      <FileText size={13} className="mr-1" /> {b.invoice_number || "Brouillon"}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Main page ----------
export default function CommunicationPage() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [statusSub, setStatusSub] = useState(null);
  const [historySub, setHistorySub] = useState(null);
  const [billingSubId, setBillingSubId] = useState(null);
  const [deleteSub, setDeleteSub] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (isPushSupported()) {
      getPushSubscription().then((sub) => setPushEnabled(!!sub)).catch(() => {});
    }
  }, []);

  const togglePush = async () => {
    try {
      setPushLoading(true);
      if (pushEnabled) {
        await disablePush();
        setPushEnabled(false);
        toast.success("Notifications push désactivées");
      } else {
        await enablePush();
        setPushEnabled(true);
        toast.success("Notifications push activées sur cet appareil");
        sendTestPush().catch(() => {});
      }
    } catch (err) {
      toast.error(err.message || "Impossible d'activer les notifications");
    } finally {
      setPushLoading(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [subsRes, statsRes] = await Promise.all([getSubscriptions(), getSubscriptionStats()]);
      setSubscriptions(subsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Erreur de chargement des abonnements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async (sub, period) => {
    try {
      setGenerating(`${sub.id}-${period}`);
      const res = await generateSubscriptionInvoice(sub.id, period);
      toast.success(res.data.message, {
        action: { label: "Ouvrir la facture", onClick: () => navigate(`/document/${res.data.invoice_id}`) },
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setGenerating(null);
    }
  };

  const handleSkip = async (sub, period) => {
    try {
      await skipSubscriptionPeriod(sub.id, period);
      toast.success("Mois marqué comme passé (non facturé)");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleAction = (action, sub) => {
    if (action === "edit") { setEditSub(sub); setDialogOpen(true); }
    else if (action === "history") setHistorySub(sub);
    else if (action === "billing") setBillingSubId(sub.id);
    else if (action === "delete") setDeleteSub(sub);
    else { setStatusAction(action); setStatusSub(sub); }
  };

  const handleDelete = async () => {
    try {
      await deleteSubscription(deleteSub.id);
      toast.success("Abonnement supprimé");
      setDeleteSub(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const filtered = subscriptions.filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (search && !s.customer_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const FILTERS = [
    { key: "all", label: `Tous (${subscriptions.length})` },
    { key: "active", label: `Actifs (${subscriptions.filter(s => s.status === "active").length})` },
    { key: "paused", label: `En pause (${subscriptions.filter(s => s.status === "paused").length})` },
    { key: "stopped", label: `Arrêtés (${subscriptions.filter(s => s.status === "stopped").length})` },
  ];

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto" data-testid="communication-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone size={24} className="text-[#26788A]" /> Communication
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Forfaits mensuels sans engagement — BASIC 300€ · BRANDING 500€ · VISIBILITÉ 800€ HT</p>
        </div>
        <div className="flex items-center gap-2">
          {isPushSupported() && (
            <Button
              variant="outline"
              onClick={togglePush}
              disabled={pushLoading}
              className={pushEnabled ? "border-teal-300 text-[#26788A] bg-teal-50" : "text-slate-500"}
              data-testid="push-toggle-btn"
              title={pushEnabled ? "Notifications push activées à chaque renouvellement" : "Activer les notifications push"}
            >
              {pushLoading ? <Loader2 size={16} className="animate-spin" /> : pushEnabled ? <Bell size={16} /> : <BellOff size={16} />}
              <span className="hidden sm:inline ml-1.5">{pushEnabled ? "Notifications ON" : "Notifications"}</span>
            </Button>
          )}
          <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={() => { setEditSub(null); setDialogOpen(true); }} data-testid="new-subscription-btn">
            <Plus size={16} className="mr-1" /> Ajouter des forfaits
          </Button>
        </div>
      </div>

      <CommTabs />

      <StatsBar stats={stats} />

      <BillingDueSection subscriptions={subscriptions} onGenerate={handleGenerate} onSkip={handleSkip} generating={generating} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-lg">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${statusFilter === f.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-xs ml-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher un client..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="subscription-search-input" />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-slate-300" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl" data-testid="empty-state">
          <Megaphone size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">{subscriptions.length === 0 ? "Aucun abonnement pour le moment" : "Aucun résultat pour ce filtre"}</p>
          {subscriptions.length === 0 && (
            <Button variant="outline" className="mt-4" onClick={() => { setEditSub(null); setDialogOpen(true); }}>
              <Plus size={15} className="mr-1" /> Créer le premier abonnement
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(filtered.reduce((groups, sub) => {
            (groups[sub.customer_id] ||= []).push(sub);
            return groups;
          }, {})).map(([customerId, subs]) => (
            <section key={customerId} className="space-y-3" data-testid={`client-subscriptions-${customerId}`}>
              {subs.length > 1 && <div className="flex flex-wrap items-center justify-between gap-2 pt-4 pb-1 border-b border-slate-200" data-testid={`client-subscriptions-summary-${customerId}`}>
                <h2 className="min-w-0 text-base font-semibold text-slate-900 break-words [overflow-wrap:anywhere]">{subs[0].customer_name}</h2>
                <span className="text-xs text-slate-500">{subs.length} forfaits · {fmtEur(subs.filter(s => s.status === "active").reduce((sum, s) => sum + s.monthly_price_ht, 0))} HT/mois actifs</span>
              </div>}
              {subs.map(sub => <SubscriptionCard key={sub.id} sub={sub} onAction={handleAction} />)}
            </section>
          ))}
        </div>
      )}

      {/* Dialogs */}
      {editSub ? <SubscriptionDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} editSub={editSub} /> : <SubscriptionBatchDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} />}
      <StatusDialog action={statusAction} sub={statusSub} onClose={() => { setStatusAction(null); setStatusSub(null); }} onSaved={load} />
      <HistoryDialog sub={historySub} onClose={() => setHistorySub(null)} />
      <MonthBillingDialog
        sub={subscriptions.find((s) => s.id === billingSubId) || null}
        onClose={() => setBillingSubId(null)}
        onChanged={load}
      />

      <AlertDialog open={!!deleteSub} onOpenChange={(v) => !v && setDeleteSub(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet abonnement ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'abonnement de <strong>{deleteSub?.customer_name}</strong> et tout son historique seront supprimés. Les factures déjà générées ne seront pas supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="confirm-delete-btn">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
